import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Clock, CheckCircle2 } from 'lucide-react';

export const STATUS_LABELS = {
  pending: 'Pending',
  paid: 'Paid',
  'in-design': 'In Design',
  proofing: 'Proofing',
  shipped: 'Shipped',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
};

export const OrderCard = ({ order, submitted }) => (
  <Card className="border-stone-taupe/20" data-testid={`order-card-${order.orderNumber}`}>
    <CardHeader>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <CardTitle className="text-charcoal-ink">Order #{order.orderNumber}</CardTitle>
          <CardDescription>
            Placed on {order.createdAt ? new Date(order.createdAt).toLocaleDateString() : '—'}
          </CardDescription>
        </div>
        {submitted ? (
          <Badge className="bg-green-700 text-white">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            {STATUS_LABELS[order.status] || 'Design Details Submitted'}
          </Badge>
        ) : (
          <Badge className="bg-charcoal-ink text-warm-ivory">
            <Clock className="w-3 h-3 mr-1" />
            {order.status === 'paid' ? 'Pending Design Details' : (STATUS_LABELS[order.status] || 'Pending')}
          </Badge>
        )}
      </div>
    </CardHeader>
    <CardContent>
      <div className="space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-stone-taupe mb-1">Customer</p>
            <p className="text-charcoal-ink font-medium">{order.customerInfo?.name}</p>
            <p className="text-charcoal-ink text-sm">{order.customerInfo?.email}</p>
          </div>
          <div>
            <p className="text-sm text-stone-taupe mb-1">Total</p>
            <p className="text-2xl font-bold text-charcoal-ink">${Number(order.total).toFixed(2)}</p>
          </div>
        </div>

        <div>
          <p className="text-sm text-stone-taupe mb-2">Items</p>
          <div className="space-y-2">
            {order.items?.map((item) => (
              <div key={`${item.id}-${item.priceType}`} className="flex items-center gap-3 bg-soft-linen p-3 rounded-lg">
                {item.image && <img src={item.image} alt={item.name} className="w-12 h-12 object-cover rounded" />}
                <div className="flex-grow">
                  <p className="font-medium text-charcoal-ink">{item.name}</p>
                  <p className="text-sm text-stone-taupe capitalize">{item.priceType} • Qty: {item.quantity}</p>
                </div>
                <p className="font-semibold text-charcoal-ink">${(item.price * item.quantity).toFixed(2)}</p>
              </div>
            ))}
          </div>
        </div>

        {!submitted && (
          <div className="bg-soft-linen p-4 rounded-lg border border-stone-taupe/20">
            <p className="text-charcoal-ink font-medium mb-2">⚠️ Action Required</p>
            <p className="text-stone-taupe mb-3">Please submit your design details to begin production</p>
            <Link to="/design-details">
              <Button
                className="bg-charcoal-ink hover:bg-charcoal-ink/90 text-warm-ivory"
                onClick={() => sessionStorage.setItem('lastOrder', JSON.stringify(order))}
              >
                Complete Design Details Form
              </Button>
            </Link>
          </div>
        )}
      </div>
    </CardContent>
  </Card>
);
